import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useMemo, useState } from "react";
import {
  GetReviewStepQuestionsQuery,
  GetReviewStepQuestionsQueryVariables,
  ReviewQuestionAction,
} from "types/graphql-schema";

import getReviewStepQuestionsQuery from "@apps/programs/graphql/get-review-step-questions-query";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import AppLabel from "@components/draft-label/app-label";
import Dropdown from "@components/dropdown/dropdown";
import GraphqlError from "@components/error/graphql-error";
import Loading from "@components/loading/loading";
import Sidebar from "@components/sidebar/sidebar";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { alphabet } from "@helpers/constants";
import { classNames } from "@helpers/css";

import EditQuestionWordingDialog from "./edit-question-wording-dialog";
import {
  ReviewQuestionRow,
  ReviewQuestionScope,
  buildReviewSectionRows,
  findReviewStepTemplate,
  reviewStepTemplateFlags,
} from "./review-question-rows";
import useChangeReviewQuestion from "./use-change-review-question";

const removeSectionConfirmation = {
  title: "Remove this section?",
  message:
    'People will no longer see this section. Their answers stay saved. To bring the section back, choose "Show again" in its menu.',
};

/**
 * The question side panel of a published review (TF-1697).
 *
 * It lists one section of a step: its inner sections and questions, as this review shows
 * them. Nothing here edits the shared library. Each inner section and question has a menu
 * to remove it for this review, and each question can get new wording. Removed ones stay
 * listed, dimmed, with "Show again".
 *
 * The talent indicators section is removed as a whole (its link is marked removed); its
 * indicators can be removed one by one but never reworded.
 */
const ReviewQuestionsSidebar = ({
  review,
  questionSetId,
  onClose,
}: {
  review: ReviewQuestionScope;
  questionSetId: number;
  onClose: () => void;
}) => {
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const [questionToEdit, setQuestionToEdit] =
    useState<ReviewQuestionRow | null>(null);

  const { data, loading, error } = useQuery<
    GetReviewStepQuestionsQuery,
    GetReviewStepQuestionsQueryVariables
  >(getReviewStepQuestionsQuery, {
    variables: { complianceProgramId: review.complianceProgramId },
  });
  const {
    change,
    loading: isSaving,
    ConfirmationDialog,
  } = useChangeReviewQuestion(review);

  const template = findReviewStepTemplate(
    data?.complianceProgram,
    review.assessmentTemplateId,
  );
  const link =
    template?.questionSetLinks.find(
      ({ questionSet }) => questionSet.id === questionSetId,
    ) ?? null;
  const sections = useMemo(
    () =>
      link && template
        ? buildReviewSectionRows(link, template.questionOverrides)
        : [],
    [link, template],
  );

  if (!data && loading) {
    return (
      <Sidebar
        wide
        className="bg-gray-50"
        headerClassName="bg-gray-50"
        onClose={onClose}
      >
        <div className="flex items-center justify-center h-64">
          <Loading>Loading questions...</Loading>
        </div>
      </Sidebar>
    );
  }

  const isTalentIndicators = !!link?.isTalentIndicators;

  // The talent indicators section goes as a whole, through its link; any other section
  // hides its questions for this review. Talent indicators hidden one by one come back
  // like any other section: their link was never removed.
  const sectionOptions = (sectionId: number, removed: boolean) => {
    if (!link) {
      return null;
    }
    if (link.removed || (isTalentIndicators && !removed)) {
      return removed
        ? {
            text: "Show again",
            onClick: () =>
              change(
                {
                  action: ReviewQuestionAction.ShowQuestionSet,
                  questionSetId: link.questionSet.id,
                },
                { successTitle: "Section shown again" },
              ),
          }
        : {
            text: "Remove section",
            onClick: () =>
              change(
                {
                  action: ReviewQuestionAction.RemoveQuestionSet,
                  questionSetId: link.questionSet.id,
                },
                {
                  successTitle: "Section removed",
                  confirmation: removeSectionConfirmation,
                },
              ),
          };
    }
    return removed
      ? {
          text: "Show again",
          onClick: () =>
            change(
              { action: ReviewQuestionAction.ShowSection, sectionId },
              { successTitle: "Section shown again" },
            ),
        }
      : {
          text: "Remove section",
          onClick: () =>
            change(
              { action: ReviewQuestionAction.RemoveSection, sectionId },
              {
                successTitle: "Section removed",
                confirmation: removeSectionConfirmation,
              },
            ),
        };
  };

  return (
    <Sidebar
      wide
      title={isTalentIndicators ? "Talent indicators" : link?.questionSet.title}
      className="bg-gray-50"
      headerClassName="bg-gray-50"
      onClose={onClose}
      additionalActions={
        <div className="flex items-center gap-2">
          {/* A change takes about a second to save and reload: show it is under way. */}
          {isSaving && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Loading mini size="4" />
              Saving...
            </div>
          )}
          <Button theme={buttonTheme.text} text="Close" onClick={onClose} />
        </div>
      }
    >
      <ConfirmationDialog />
      {questionToEdit && (
        <EditQuestionWordingDialog
          question={questionToEdit}
          review={review}
          templateFlags={reviewStepTemplateFlags(
            data?.complianceProgram,
            review.assessmentTemplateId,
          )}
          loading={isSaving}
          onClose={() => setQuestionToEdit(null)}
          onSave={async (variables) => {
            if (await change(variables, { successTitle: "Wording changed" })) {
              setQuestionToEdit(null);
            }
          }}
        />
      )}
      <Callout className="mx-6 mb-6" size="small" icon="info">
        {`This ${nounLower} is published. ${
          isTalentIndicators
            ? `You can remove this section or one indicator. Talent indicators keep their wording, so they compare the same from one ${nounLower} to the next.`
            : "You can remove a section or a question, or change a question's wording."
        } People see the change the next time they open the ${nounLower}.`}
      </Callout>
      {/* After a failed refetch Apollo keeps the old rows: show the error, not rows that may be stale. */}
      {error ? (
        <GraphqlError
          className="mx-6"
          error={error}
          whatDidNotWork="The questions could not be loaded."
        />
      ) : !link ? (
        <div className="px-6 text-sm text-gray-500">
          {`This section is not part of this ${nounLower}.`}
        </div>
      ) : (
        sections.map((section, sectionIndex) => {
          const action = sectionOptions(section.sectionId, section.removed);
          return (
            <div
              key={section.sectionId}
              className="border-t p-6"
              aria-label={`Section ${alphabet[sectionIndex]}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div
                  className={classNames(
                    "flex items-center gap-2 min-w-0",
                    section.removed && "opacity-50",
                  )}
                >
                  <div className="text-lg font-semibold shrink-0">{`Section ${alphabet[sectionIndex]}`}</div>
                  {section.title && (
                    <div className="text-lg truncate">{section.title}</div>
                  )}
                  {section.removed && <AppLabel label="Removed" />}
                </div>
                {action && (
                  <Dropdown
                    align="end"
                    disabled={isSaving}
                    aria-label="Section menu"
                    options={[{ label: action.text, onClick: action.onClick }]}
                  />
                )}
              </div>
              {!isEmptyValue(section.description) && (
                <div className={classNames(section.removed && "opacity-50")}>
                  <TextareaWysiwyg
                    className="mt-2"
                    fullWidth
                    value={section.description}
                  />
                </div>
              )}
              <div className="mt-6 flex flex-col gap-4">
                {section.questions.map((question, questionIndex) => (
                  <div
                    key={question.questionId}
                    className="bg-white border rounded-sm p-4 text-sm"
                    aria-label="Question"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div
                        className={classNames(
                          "flex items-start gap-3 min-w-0",
                          question.removed && "opacity-50",
                        )}
                      >
                        <div className="py-0.5 px-2 rounded-sm bg-green-100 font-bold shrink-0">{`${
                          alphabet[sectionIndex]
                        }${questionIndex + 1}`}</div>
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{question.title}</div>
                            {question.removed && <AppLabel label="Removed" />}
                          </div>
                          {question.changed && (
                            <div className="text-xs text-gray-500">
                              {`Changed for this ${nounLower}`}
                            </div>
                          )}
                          {!isEmptyValue(question.description) && (
                            <TextareaWysiwyg
                              className="text-gray-600"
                              fullWidth
                              value={question.description}
                              // The editor reads `value` once: rebuild it when the wording changes.
                              deps={[question.description]}
                            />
                          )}
                        </div>
                      </div>
                      {/* A question of a removed set comes back with its set. */}
                      {question.removed && !link.removed ? (
                        <Dropdown
                          align="end"
                          disabled={isSaving}
                          aria-label="Question menu"
                          options={[
                            {
                              label: "Show again",
                              onClick: () =>
                                change(
                                  {
                                    action: ReviewQuestionAction.ShowQuestion,
                                    questionId: question.questionId,
                                  },
                                  { successTitle: "Question shown again" },
                                ),
                            },
                          ]}
                        />
                      ) : (
                        !question.removed && (
                          <Dropdown
                            align="end"
                            disabled={isSaving}
                            aria-label="Question menu"
                            options={compact([
                              {
                                label: "Remove question",
                                onClick: () =>
                                  change(
                                    {
                                      action: ReviewQuestionAction.HideQuestion,
                                      questionId: question.questionId,
                                    },
                                    {
                                      successTitle: "Question removed",
                                      confirmation: {
                                        title: "Remove this question?",
                                        message:
                                          'People will no longer see this question. Their answers stay saved. To bring the question back, choose "Show again" in its menu.',
                                      },
                                    },
                                  ),
                              },
                              !question.isTalentIndicator && {
                                label: "Edit wording",
                                onClick: () => setQuestionToEdit(question),
                              },
                              question.changed && {
                                label: "Use original wording",
                                onClick: () =>
                                  change(
                                    {
                                      action:
                                        ReviewQuestionAction.RestoreWording,
                                      questionId: question.questionId,
                                    },
                                    {
                                      successTitle: "Original wording restored",
                                      confirmation: {
                                        title: "Use the original wording?",
                                        message: `The wording written for this ${nounLower} is dropped. People see the question as it was before.`,
                                      },
                                    },
                                  ),
                              },
                            ])}
                          />
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </Sidebar>
  );
};

export default ReviewQuestionsSidebar;
